import React from 'react';
import { Link } from 'react-router-dom';
import { useTenant } from '../../../context/TenantContext';
import { useMarketingCourses } from '../MarketingData';
import { formatPrice } from '../../../lib/format';
import type { PublicCourse } from '../../../services/publicService';

export const UtilityMatrix: React.FC = () => {
  const { tenant } = useTenant();
  const tenantMatrix = tenant?.branding.marketing?.matrix;
  const { courses, loaded } = useMarketingCourses();

  const rows: MatrixRow[] = tenantMatrix && tenantMatrix.length > 0
    ? tenantMatrix.map(m => ({
        id: m.id, title: m.title, subtitle: m.subtitle,
        level: m.level ?? 2, track: m.track, duration: m.duration, price: m.price,
      }))
    : courses.slice(0, 6).map((c, i) => coursToRow(c, i));

  if (!loaded || rows.length === 0) return null;

  return (
    <section id="matrix" className="util-matrix py-20 bg-[var(--util-paper)] border-b border-[var(--util-rule)]">
      <div className="max-w-[1320px] mx-auto px-6">
        <div className="grid md:grid-cols-[220px_1fr] gap-10 mb-12 items-start">
          <div className="font-util-mono text-[11px] uppercase tracking-[0.06em] text-[var(--util-ink-faint)] border-t-2 border-[var(--util-ink)] pt-3">
            <span className="block text-[var(--util-accent-ink)] mb-1.5 font-medium">§ 02</span>
            Catalogue
          </div>
          <div>
            <h2 className="font-util-sans text-3xl md:text-[44px] font-semibold tracking-[-0.025em] leading-[1.05] max-w-[24ch]">
              Twelve programmes, four specialisations.
            </h2>
            <p className="text-[var(--util-ink-soft)] text-base max-w-[60ch] mt-3">
              Tracks interlock — most students take 2–3 over a year. Levels indicate prerequisite depth, not difficulty.
            </p>
          </div>
        </div>

        <div className="border border-[var(--util-rule)] bg-white rounded-md overflow-hidden">
          <div
            className="grid bg-[var(--util-mute)] font-util-mono text-[11px] tracking-[0.04em] uppercase text-[var(--util-ink-soft)] border-b border-[var(--util-rule)]"
            style={{ gridTemplateColumns: '60px 1.4fr repeat(4, minmax(0, 1fr))' }}
          >
            <div className="px-4 py-3">ID</div>
            <div className="px-4 py-3">Programme</div>
            <div className="px-4 py-3 hidden md:block">Level</div>
            <div className="px-4 py-3 hidden md:block">Track</div>
            <div className="px-4 py-3 hidden md:block">Duration</div>
            <div className="px-4 py-3 hidden md:block">Price</div>
          </div>
          {rows.map((row, i) => (
            <Link
              key={i}
              to={tenantMatrix ? '#' : `/course/${courses[i]?.id ?? ''}`}
              className="util-matrix-row grid border-b border-[#e5e3dd] last:border-b-0 transition-colors hover:bg-[var(--util-mute)]"
              style={{ gridTemplateColumns: '60px 1.4fr repeat(4, minmax(0, 1fr))' }}
            >
              <div className="px-4 py-4 flex items-center font-util-mono text-[11px] text-[var(--util-ink-faint)]">
                {row.id}
              </div>
              <div className="px-4 py-4">
                <div className="font-semibold text-[14px] text-[var(--util-ink)]">{row.title}</div>
                {row.subtitle && (
                  <div className="text-[12px] text-[var(--util-ink-soft)] mt-0.5">{row.subtitle}</div>
                )}
              </div>
              <div className="px-4 py-4 hidden md:flex items-center">
                <Level n={row.level ?? 2} />
              </div>
              <div className="px-4 py-4 hidden md:flex items-center">
                {row.track && (
                  <span className="font-util-mono text-[11px] bg-[var(--util-accent-soft)] text-[var(--util-accent-ink)] px-2 py-0.5 rounded">
                    {row.track}
                  </span>
                )}
              </div>
              <div className="px-4 py-4 hidden md:flex items-center font-util-mono text-[12px] text-[var(--util-ink)]">
                {row.duration || '—'}
              </div>
              <div className="px-4 py-4 hidden md:flex items-center font-util-mono text-[13px] font-semibold text-[var(--util-ink)]">
                {row.price || '—'}
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-6 flex justify-between items-center font-util-mono text-[12px] text-[var(--util-ink-faint)]">
          <span>showing {rows.length} {rows.length === 1 ? 'row' : 'rows'}</span>
          <Link
            to="/courses"
            className="text-[var(--util-accent-ink)] border-b border-[var(--util-accent)] pb-px hover:opacity-80 transition-opacity"
          >
            view full catalogue →
          </Link>
        </div>
      </div>
    </section>
  );
};

interface MatrixRow {
  id: string;
  title: string;
  subtitle?: string;
  level?: number;
  track?: string;
  duration?: string;
  price?: string;
}

const Level: React.FC<{ n: number }> = ({ n }) => (
  <span className="inline-flex gap-[2px]">
    {[1, 2, 3, 4].map(i => (
      <span
        key={i}
        className={`inline-block w-[14px] h-[6px] rounded-[1px] ${
          i <= n ? 'bg-[var(--util-accent)]' : 'bg-[var(--util-mute-2)]'
        }`}
      />
    ))}
  </span>
);

/** Generate a course ID like `DS-204` from category + index. */
function coursToRow(c: PublicCourse, i: number): MatrixRow {
  const prefix = (c.category || 'CR').slice(0, 3).toUpperCase();
  const num = 100 + i * 10;
  const levelMap: Record<string, number> = { Beginner: 1, Intermediate: 2, Advanced: 3, Expert: 4 };
  return {
    id: `${prefix}-${num}`,
    title: c.title,
    subtitle: c.description?.slice(0, 80),
    level: levelMap[c.level] ?? 2,
    track: c.category,
    duration: '—',
    price: c.price > 0 ? formatPrice(c.price) : 'Free',
  };
}

